/**
 * Phase 2 progression rules (12 §2–§3), pure and shared by client and server:
 * slots by level, XP curve, per-match XP, kit unlocks, and the disjoint-key
 * rule that keeps a three-slot disclord wider, never stronger on one axis.
 * Nothing here touches the sim — tuning still resolves through `resolveTuning`.
 */
import { overlaysFor } from '../sim/powerups.js'
import type { MatchKind, PlayerStats, Team } from '../sim/types.js'

/** Slot k (1-based) opens at SLOT_LEVELS[k-1]: 1 at 0, 2 at 10, 3 at 20 (12 §2). */
export const SLOT_LEVELS: readonly number[] = [0, 10, 20]
export const MAX_LEVEL = 30
export const slotsAt = (level: number): number => SLOT_LEVELS.filter((l) => level >= l).length

/** XP needed to go from level L to L+1 (12 §3). */
export const XP_TO_NEXT = (level: number): number => 400 + 120 * level

export interface LevelProgress { level: number; into: number; toNext: number }
export function levelProgress(xp: number): LevelProgress {
  let level = 0, rest = Math.max(0, Math.floor(xp))
  while (level < MAX_LEVEL && rest >= XP_TO_NEXT(level)) { rest -= XP_TO_NEXT(level); level++ }
  return { level, into: level >= MAX_LEVEL ? 0 : rest, toNext: level >= MAX_LEVEL ? 0 : XP_TO_NEXT(level) }
}
export const levelFor = (xp: number): number => levelProgress(xp).level

/** Kit unlocks by level (12 §3); Warden-tier unlocks come with runs (Sprint 9). */
export const KIT_UNLOCKS: Readonly<Record<string, number>> = Object.freeze({ intercept: 4, curveball: 8, anchor: 12, ricochet_sense: 16 })
export const unlockedAt = (level: number): string[] => Object.entries(KIT_UNLOCKS).filter(([, l]) => level >= l).map(([id]) => id)

export const XP = Object.freeze({ PVP_PLAYED: 40, PVP_WON: 60, ROUND_WON: 15, ELIM: 5, BANK_ELIM_BONUS: 5, ELIM_CAP: 60, PRACTICE_PLAYED: 10 })
/** 12 §3 run rows: bout cleared 50 × index; run won 300 + 100 × tier; run failed banks 25 % of bout XP. */
export const RUN_XP = Object.freeze({ BOUT_BASE: 50, WON_BASE: 300, WON_PER_TIER: 100, FAILED_FRACTION: 0.25 })
export const boutXp = (index: number): number => RUN_XP.BOUT_BASE * index
export const runWonXp = (tier: number): number => RUN_XP.WON_BASE + RUN_XP.WON_PER_TIER * tier
/** Run lifetime cap (12 §4: ≤ 30 min) and the card pick window (reuses the 06 §4 deck timing). */
export const RUN_MAX_MS = 30 * 60_000
export const RUN_CARD_PICK_MS = 20_000
export const RUN_BOUTS = 3

export interface MatchXpInput {
  kind: MatchKind
  winner: Team | null
  rounds: readonly { winner: Team | null; void: boolean }[]
  team: Team
  stats: Pick<PlayerStats, 'eliminations' | 'bankEliminations'>
}
/** XP for one participant of a finished match (12 §3 table). Practice (vs BOT) matches award only the flat 10. */
export function matchXp(m: MatchXpInput): number {
  if (m.kind === 'practice') return XP.PRACTICE_PLAYED
  if (m.kind === 'bout' || m.kind === 'warden') return 0 // the run awards these (12 §3 run rows)
  let xp = XP.PVP_PLAYED
  if (m.winner === m.team) xp += XP.PVP_WON
  xp += XP.ROUND_WON * m.rounds.filter((r) => !r.void && r.winner === m.team).length
  const elim = Math.min(XP.ELIM_CAP, m.stats.eliminations * XP.ELIM + m.stats.bankEliminations * XP.BANK_ELIM_BONUS)
  return xp + elim
}

/** Tuning keys a power-up touches (buff ∪ nerf). Unknown ids touch nothing. */
export const keysOf = (id: string): string[] => Object.keys(overlaysFor([id])[0] ?? {})
/** Keys two power-ups both override — non-empty means they cannot share a build (12 §2). */
export function clashingKeys(a: string, b: string): string[] {
  const kb = new Set(keysOf(b))
  return keysOf(a).filter((k) => kb.has(k)).sort()
}
/** The disjoint-key rule: no tuning key may be overridden by two slotted power-ups. */
export function slotsCompatible(ids: readonly string[]): boolean {
  for (let i = 0; i < ids.length; i++) for (let j = i + 1; j < ids.length; j++) if (clashingKeys(ids[i], ids[j]).length) return false
  return true
}
